import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectCartItems, selectCartTotal, clearCart } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import Layout from '../components/Layout';
import './Order.css';

const Order = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector(selectCartItems);
  const cartTotal = useAppSelector(selectCartTotal);
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);

  const [orderItems, setOrderItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    address: '',
    city: '',
    state: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if coming from "Buy Now" (single item or multiple items with sizes) or cart (multiple items)
    const buyNowItem = location.state?.buyNowItem;
    const buyNowItems = location.state?.buyNowItems;
    
    if (buyNowItems && buyNowItems.length > 0) {
      // Multiple items with sizes from Buy Now
      setOrderItems(buyNowItems);
    } else if (buyNowItem) {
      // Single item order
      setOrderItems([{ ...buyNowItem, quantity: buyNowItem.quantity || 1 }]);
    } else if (cartItems.length > 0) {
      // Multiple items from cart
      setOrderItems(cartItems);
    } else {
      // No items, redirect to products
      navigate('/products');
    }
  }, [location.state, cartItems, navigate]);

  const handleShippingChange = (e) => {
    setShippingInfo({
      ...shippingInfo,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateShipping = () => {
    const newErrors = {};
    
    if (!shippingInfo.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!shippingInfo.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!shippingInfo.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!shippingInfo.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!shippingInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    const subtotalRmb = orderItems.reduce((sum, item) => {
      return sum + ((item.price || 0) * item.quantity);
    }, 0);
    const shippingNaira = 2000; // Fixed shipping in Naira for display
    return {
      subtotalRmb,
      shippingNaira,
      totalRmb: subtotalRmb,
    };
  };

  const totals = calculateTotal();
  const displaySubtotal = toDisplayNaira(totals.subtotalRmb, nairaPerYuan);
  const displayTotal = displaySubtotal + totals.shippingNaira;

  const formatPrice = (priceInYuan) => {
    return formatNaira(toDisplayNaira(priceInYuan, nairaPerYuan));
  };

  const handlePlaceOrder = async () => {
    if (!validateShipping()) {
      return;
    }

    // TODO: Send order to backend
    // const orderData = {
    //   items: orderItems,
    //   shippingInfo,
    //   paymentMethod,
    //   total: totals.total
    // };
    // const response = await fetch('/api/orders', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(orderData)
    // });

    // Clear cart after successful order
    dispatch(clearCart());
    
    // Navigate to order confirmation (total in Naira for display; backend receives RMB)
    navigate('/order-confirmation', { 
      state: { 
        orderId: `ORD-${Date.now()}`,
        total: displayTotal 
      } 
    });
  };

  if (orderItems.length === 0) {
    return null;
  }

  return (
    <Layout>
      <div className="order-page">
        <div className="order-container">
          <h1>Checkout</h1>

          <div className="order-content">
            <div className="order-left">
              {/* Shipping Information */}
              <section className="order-section">
                <h2>Shipping Information</h2>
                <div className="shipping-form">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={shippingInfo.fullName}
                      onChange={handleShippingChange}
                      className={errors.fullName ? 'error' : ''}
                      placeholder="Enter your full name"
                    />
                    {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={shippingInfo.phone}
                      onChange={handleShippingChange}
                      className={errors.phone ? 'error' : ''}
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={shippingInfo.address}
                      onChange={handleShippingChange}
                      className={errors.address ? 'error' : ''}
                      placeholder="Street address"
                    />
                    {errors.address && <span className="error-message">{errors.address}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={shippingInfo.city}
                        onChange={handleShippingChange}
                        className={errors.city ? 'error' : ''}
                        placeholder="City"
                      />
                      {errors.city && <span className="error-message">{errors.city}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={shippingInfo.state}
                        onChange={handleShippingChange}
                        className={errors.state ? 'error' : ''}
                        placeholder="State"
                      />
                      {errors.state && <span className="error-message">{errors.state}</span>}
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment Method */}
              <section className="order-section">
                <h2>Payment Method</h2>
                <div className="payment-methods">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">💳</span>
                      <span>Credit/Debit Card</span>
                    </div>
                  </label>

                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">🏦</span>
                      <span>Bank Transfer</span>
                    </div>
                  </label>

                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <div className="payment-option-content">
                      <span className="payment-icon">💵</span>
                      <span>Cash on Delivery</span>
                    </div>
                  </label>
                </div>
              </section>
            </div>

            {/* Order Summary */}
            <div className="order-right">
              <div className="order-summary">
                <h2>Order Summary</h2>
                
                <div className="order-items">
                  {orderItems.map(item => (
                    <div key={item.id} className="order-item">
                      <div className="order-item-image">
                        <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name} />
                      </div>
                      <div className="order-item-details">
                        <h3>{item.name}</h3>
                        <p>Quantity: {item.quantity}</p>
                        <p className="order-item-price">{formatPrice((item.price || 0) * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-totals">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>{formatNaira(displaySubtotal)}</span>
                  </div>
                  <div className="total-row">
                    <span>Shipping:</span>
                    <span>{formatNaira(totals.shippingNaira)}</span>
                  </div>
                  <div className="total-row total">
                    <span>Total:</span>
                    <span>{formatNaira(displayTotal)}</span>
                  </div>
                </div>

                <button onClick={handlePlaceOrder} className="place-order-btn">
                  Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Order;
